/**
 * SCCM export import flow.
 *
 * For tenants onboarded in SCCM mode: no Intune connection, so the estate
 * arrives as the three CSV exports produced by the ALICE/AppRat SQL
 * queries rather than from a Graph sync.
 *
 * The flow is deliberately two-step. The three files must come from the
 * same export run, and the backend's reconciliation against SCCM's own
 * Total_Inst is the cheapest way to catch that they did not — so we
 * always preview (which writes nothing) before allowing the import.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useAuth, API } from "@/App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertTriangle, CheckCircle2, FileSpreadsheet, Loader2, Server, Upload, X,
} from "lucide-react";

// The three exports, in the order an operator produces them.
const FILE_SLOTS = [
  {
    key: "arp",
    label: "ARP.csv",
    title: "Applications",
    hint: "One row per application. Needs AppHash and Display Name.",
  },
  {
    key: "ridarp",
    label: "RIDARP.csv",
    title: "Installs",
    hint: "One row per device/application install. Needs resourceid and import_hash.",
  },
  {
    key: "devices",
    label: "Devices.csv",
    title: "Devices",
    hint: "One row per device. Needs ResourceID.",
  },
];

// How often to ask the backend how the background import is getting on.
// Slow enough not to hammer a tenant database that is mid-bulk-insert.
const POLL_INTERVAL_MS = 2500;

const formatBytes = (bytes) => {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const StatTile = ({ label, value, tone = "default" }) => (
  <div className="rounded-lg bg-white/5 border border-white/10 p-3">
    <div className="text-[10px] uppercase tracking-wider text-white/60">{label}</div>
    <div
      className={`text-xl font-semibold ${
        tone === "warn" ? "text-amber-300" : "text-white"
      }`}
    >
      {value?.toLocaleString?.() ?? value ?? 0}
    </div>
  </div>
);

const FileSlot = ({ slot, file, onPick, onClear, disabled }) => {
  const inputRef = useRef(null);
  return (
    <div className="rounded-lg border border-white/10 bg-white/5 p-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <FileSpreadsheet className="h-4 w-4 text-sky-300 flex-shrink-0" />
            <span className="text-sm font-medium text-white">{slot.title}</span>
            <code className="text-[10px] bg-white/10 px-1.5 py-0.5 rounded text-white/70">
              {slot.label}
            </code>
          </div>
          <p className="text-xs text-white/60 mt-1">{slot.hint}</p>
          {file && (
            <p className="text-xs text-emerald-300 mt-1.5 truncate" title={file.name}>
              {file.name} <span className="text-white/50">({formatBytes(file.size)})</span>
            </p>
          )}
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          {file && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onClear}
              disabled={disabled}
              title="Remove file"
              data-testid={`sccm-clear-${slot.key}`}
            >
              <X className="h-3.5 w-3.5" />
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => inputRef.current?.click()}
            disabled={disabled}
            data-testid={`sccm-pick-${slot.key}`}
          >
            {file ? "Change" : "Choose"}
          </Button>
        </div>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept=".csv,text/csv"
        className="hidden"
        data-testid={`sccm-input-${slot.key}`}
        onChange={(e) => {
          const picked = e.target.files?.[0];
          if (picked) onPick(picked);
          // Reset so re-picking the same filename still fires onChange.
          e.target.value = "";
        }}
      />
    </div>
  );
};

export default function SccmImportDialog({ open, onOpenChange, onImported }) {
  const { getAccessToken } = useAuth();
  const [files, setFiles] = useState({ arp: null, ridarp: null, devices: null });
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(null); // "preview" | "import" | null
  const [error, setError] = useState(null);
  // Live server-side progress while the background import runs.
  const [progress, setProgress] = useState(null);
  // Set when the dialog closes or resets, so a poll in flight stops
  // rather than writing state into an unmounted dialog.
  const stopPolling = useRef(false);

  const allChosen = FILE_SLOTS.every((s) => files[s.key]);

  const reset = useCallback(() => {
    stopPolling.current = true;
    setFiles({ arp: null, ridarp: null, devices: null });
    setPreview(null);
    setResult(null);
    setError(null);
    setBusy(null);
    setProgress(null);
  }, []);

  // Never leave a poll running against an unmounted dialog.
  useEffect(() => () => {
    stopPolling.current = true;
  }, []);

  const pick = (key) => (file) => {
    setFiles((prev) => ({ ...prev, [key]: file }));
    // Any file change invalidates the previous preview — importing against
    // a stale report would defeat the point of previewing.
    setPreview(null);
    setResult(null);
    setError(null);
  };

  const clear = (key) => () => {
    setFiles((prev) => ({ ...prev, [key]: null }));
    setPreview(null);
    setResult(null);
  };

  const buildFormData = () => {
    const fd = new FormData();
    FILE_SLOTS.forEach((s) => fd.append(s.key, files[s.key]));
    return fd;
  };

  const readError = (err) => {
    const detail = err?.response?.data?.detail;
    if (typeof detail === "string") return detail;
    if (detail?.message) return detail.message;
    return err?.message || "Something went wrong.";
  };

  const runPreview = async () => {
    setBusy("preview");
    setError(null);
    setResult(null);
    try {
      const token = await getAccessToken();
      const response = await axios.post(
        `${API}/discover/sccm-import/preview`,
        buildFormData(),
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setPreview(response.data);
    } catch (err) {
      setPreview(null);
      setError(readError(err));
    } finally {
      setBusy(null);
    }
  };

  /**
   * Follow the import to completion.
   *
   * The import request only queues the work -- writing tens of thousands
   * of applications and rematching them against Winget takes minutes,
   * which no gateway will hold an upload connection open for. The
   * backend reports progress on the same sync-status document the Intune
   * sync uses, so we poll that until it settles.
   */
  const pollUntilDone = async () => {
    while (!stopPolling.current) {
      await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
      if (stopPolling.current) return;

      let status;
      try {
        const token = await getAccessToken();
        const response = await axios.get(`${API}/discover/sync-status`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        status = response.data;
      } catch (err) {
        // A blip fetching status is not an import failure -- the work is
        // running server-side either way, so keep watching.
        continue;
      }

      if (stopPolling.current) return;
      setProgress(status);

      if (status.status === "completed") {
        setResult(status);
        setProgress(null);
        setBusy(null);
        toast.success(`Imported ${status.imported ?? status.total_apps} applications from SCCM export`);
        onImported?.(status);
        return;
      }
      if (status.status === "failed" || status.status === "cancelled") {
        setProgress(null);
        setBusy(null);
        setError(status.error || "The import did not finish. Check the server logs.");
        return;
      }
    }
  };

  const runImport = async () => {
    setBusy("import");
    setError(null);
    setProgress(null);
    stopPolling.current = false;
    try {
      const token = await getAccessToken();
      const response = await axios.post(
        `${API}/discover/sccm-import`,
        buildFormData(),
        { headers: { Authorization: `Bearer ${token}` } },
      );

      setProgress({
        progress: response.data.progress ?? 5,
        current_step: `Importing ${(response.data.importing ?? 0).toLocaleString()} applications from SCCM export...`,
      });
      await pollUntilDone();
    } catch (err) {
      setProgress(null);
      setBusy(null);
      setError(readError(err));
    }
  };

  const report = result?.report || preview?.report;
  const warnings = report?.warnings || [];

  const mismatchNote = useMemo(() => {
    if (!report?.count_mismatch_total) return null;
    const shown = report.count_mismatches?.length || 0;
    return report.count_mismatch_total > shown
      ? `Showing ${shown} of ${report.count_mismatch_total}`
      : null;
  }, [report]);

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        // A preview holds the chosen files and is quick, so it must not
        // be interrupted. An import runs server-side -- closing simply
        // stops us watching it, and Discover picks up the result.
        if (busy === "preview") return;
        if (!next) reset();
        onOpenChange(next);
      }}
    >
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="sccm-import-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Server className="h-5 w-5 text-sky-400" />
            Import SCCM export
          </DialogTitle>
          <DialogDescription>
            Upload the three CSV exports from your SCCM SQL queries. ALICE checks
            them against each other first and writes nothing until you confirm.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* ---- file pickers ---- */}
          <div className="space-y-2">
            {FILE_SLOTS.map((slot) => (
              <FileSlot
                key={slot.key}
                slot={slot}
                file={files[slot.key]}
                onPick={pick(slot.key)}
                onClear={clear(slot.key)}
                disabled={!!busy || !!result}
              />
            ))}
            <p className="text-xs text-white/50">
              CSV only — save the sheet as CSV if you exported to Excel.
            </p>
          </div>

          {/* ---- error ---- */}
          {error && (
            <div
              className="rounded-lg border border-rose-500/40 bg-rose-500/10 p-3"
              data-testid="sccm-import-error"
            >
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 text-rose-300 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-rose-200">{error}</div>
              </div>
            </div>
          )}

          {/* ---- live progress while the background import runs ---- */}
          {progress && (
            <div
              className="rounded-lg border border-sky-500/40 bg-sky-500/10 p-3 space-y-2"
              data-testid="sccm-import-progress"
            >
              <div className="flex items-center justify-between text-sm">
                <span className="flex items-center gap-2 text-sky-200">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Importing
                </span>
                <span className="text-white/70">{progress.progress ?? 0}%</span>
              </div>
              <Progress value={progress.progress ?? 0} className="h-2 bg-slate-700" />
              <p className="text-xs text-white/70">
                {progress.current_step || "Working..."}
              </p>
              <p className="text-xs text-white/50">
                This runs on the server — it keeps going even if you close this
                dialog, and Discover will show the new estate when it finishes.
              </p>
            </div>
          )}

          {/* ---- report (preview or completed import) ---- */}
          {report && (
            <div className="space-y-3" data-testid="sccm-import-report">
              {result ? (
                <div className="flex items-center gap-2 text-emerald-300 text-sm font-medium">
                  <CheckCircle2 className="h-4 w-4" />
                  Imported {(result.imported ?? result.total_apps ?? 0).toLocaleString()} applications
                  {result.preserved_app_state > 0 && (
                    <span className="text-white/60 font-normal">
                      · kept your decisions on {result.preserved_app_state} app
                      {result.preserved_app_state === 1 ? "" : "s"}
                    </span>
                  )}
                </div>
              ) : progress ? null : (
                <div className="text-sm text-white/80">
                  Ready to import{" "}
                  <strong className="text-white">
                    {preview.would_import.toLocaleString()}
                  </strong>{" "}
                  applications. Nothing has been written yet.
                </div>
              )}

              <div className="alice-kpi-row grid grid-cols-2 sm:grid-cols-4 gap-2">
                <StatTile label="Applications" value={report.apps_in_arp} />
                <StatTile label="Devices" value={report.unique_devices_matched} />
                <StatTile label="Installs" value={report.install_rows} />
                <StatTile
                  label="No installs"
                  value={report.apps_without_devices}
                  tone={report.apps_without_devices ? "warn" : "default"}
                />
              </div>

              {warnings.length > 0 && (
                <div
                  className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 space-y-1"
                  data-testid="sccm-import-warnings"
                >
                  <div className="flex items-center gap-2 text-amber-200 text-sm font-medium">
                    <AlertTriangle className="h-4 w-4" />
                    Check before importing
                  </div>
                  <ul className="list-disc list-inside text-xs text-amber-100/90 space-y-0.5">
                    {warnings.map((w) => (
                      <li key={w}>{w}</li>
                    ))}
                  </ul>
                  <p className="text-[11px] text-amber-100/70 pt-1">
                    These usually mean the three files came from different export
                    runs. You can still import, but the numbers may not match what
                    SCCM reports.
                  </p>
                </div>
              )}

              {/* Count reconciliation detail */}
              {report.count_mismatch_total > 0 && (
                <details className="rounded-lg border border-white/10 bg-white/5 p-3">
                  <summary className="text-xs text-white/80 cursor-pointer">
                    {report.count_mismatch_total} application
                    {report.count_mismatch_total === 1 ? "" : "s"} where SCCM&apos;s
                    Total_Inst disagrees with the install rows
                    {mismatchNote && (
                      <span className="text-white/50"> · {mismatchNote}</span>
                    )}
                  </summary>
                  <div className="mt-2 overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead className="text-white/60">
                        <tr className="text-left">
                          <th className="py-1 pr-3 font-medium">Application</th>
                          <th className="py-1 pr-3 font-medium">Version</th>
                          <th className="py-1 pr-3 font-medium">Total_Inst</th>
                          <th className="py-1 font-medium">Install rows</th>
                        </tr>
                      </thead>
                      <tbody className="text-white/80">
                        {(report.count_mismatches || []).map((m) => (
                          <tr key={m.app_hash} className="border-t border-white/5">
                            <td className="py-1 pr-3">{m.display_name}</td>
                            <td className="py-1 pr-3 text-white/60">{m.version || "—"}</td>
                            <td className="py-1 pr-3">{m.total_inst}</td>
                            <td className="py-1">{m.join_count}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </details>
              )}

              {/* Sample so names/versions can be eyeballed pre-commit */}
              {!result && preview?.sample?.length > 0 && (
                <details className="rounded-lg border border-white/10 bg-white/5 p-3" open>
                  <summary className="text-xs text-white/80 cursor-pointer">
                    Most-installed applications — check these parsed correctly
                  </summary>
                  <div className="mt-2 overflow-x-auto">
                    <table className="w-full text-xs" data-testid="sccm-import-sample">
                      <thead className="text-white/60">
                        <tr className="text-left">
                          <th className="py-1 pr-3 font-medium">Application</th>
                          <th className="py-1 pr-3 font-medium">Version</th>
                          <th className="py-1 pr-3 font-medium">Publisher</th>
                          <th className="py-1 font-medium">Devices</th>
                        </tr>
                      </thead>
                      <tbody className="text-white/80">
                        {preview.sample.map((row, i) => (
                          <tr key={`${row.display_name}-${i}`} className="border-t border-white/5">
                            <td className="py-1 pr-3">{row.display_name}</td>
                            <td className="py-1 pr-3 text-white/60">{row.version || "—"}</td>
                            <td className="py-1 pr-3 text-white/60">{row.publisher || "—"}</td>
                            <td className="py-1">{row.device_count}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </details>
              )}

              {result?.winget_matching === "failed" && (
                <p className="text-xs text-amber-300">
                  Applications imported, but Winget matching failed — automation
                  potential will be blank until Discover is refreshed.
                </p>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          {result ? (
            <Button
              onClick={() => {
                reset();
                onOpenChange(false);
              }}
              data-testid="sccm-import-done"
            >
              Done
            </Button>
          ) : (
            <>
              <Button
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={busy === "preview"}
              >
                {busy === "import" ? "Close" : "Cancel"}
              </Button>
              <Button
                variant="outline"
                onClick={runPreview}
                disabled={!allChosen || !!busy}
                data-testid="sccm-import-preview-btn"
              >
                {busy === "preview" ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <FileSpreadsheet className="h-4 w-4 mr-2" />
                )}
                {preview ? "Re-check files" : "Check files"}
              </Button>
              <Button
                onClick={runImport}
                // Import is gated on a successful preview: the three-file
                // consistency check is the whole point of the flow.
                disabled={!preview || !!busy}
                className="bg-sky-600 hover:bg-sky-700 text-white"
                data-testid="sccm-import-confirm-btn"
              >
                {busy === "import" ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Upload className="h-4 w-4 mr-2" />
                )}
                Import {preview ? `${preview.would_import.toLocaleString()} apps` : ""}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
